/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Per-manifest verification: decode the COSE_Sign1, parse the signing cert,
 * verify the claim signature, and build signature_info.
 */

import type { SignatureInfo } from '../types'
import type { ParsedManifest } from '../model/parseManifestStore'
import { dataChild } from '../jumbf/parseJumbf'
import { decodeSign1, x5chain, coseAlg, type CoseSign1 } from '../cose/decodeSign1'
import { coseAlgMatchesKeyType } from '../cose/alg'
import { parseCertificate, type CertInfo } from '../x509/parseCert'
import { checkCertificateProfile } from '../x509/certProfile'
import { verifyCoseSign1 } from '../cose/verify'
import { buildSignatureInfo } from '../cose/signatureInfo'
import { validateTimestamp } from '../x509/verifyTimestamp'
import type { TrustStore } from '../x509/trust'

export interface ManifestVerification {
    label: string
    /** signature_info as reported in the manifest, or null if no signature box. */
    signatureInfo: SignatureInfo | null
    /** Whether the COSE claim signature verified against the leaf certificate. */
    signatureValid: boolean
    /** Signing certificate expired at the reference time (timestamp, else now). */
    expired: boolean
    /** Signing certificate not yet valid at the reference time. */
    notYetValid: boolean
    /** The reference time used for certificate validity (gated trusted-timestamp genTime, else now). */
    refTime: string
    /** The trusted time-stamp genTime (a "valid signed time-stamp" per §15.9.1), or null. */
    attestedTime: string | null
    /** Parsed leaf certificate, or null. */
    cert: CertInfo | null
    /** Whether the leaf cert meets the C2PA end-entity profile (§14.5.1.1). */
    profileValid: boolean
    /** Decoded COSE_Sign1, or null. */
    sign1: CoseSign1 | null
    /** The DER cert chain (leaf first). */
    chain: Uint8Array[]
}

/** Options controlling how the COSE timestamp gates the certificate-validity reference time. */
export interface VerifyManifestOptions {
    /**
     * When set, the timestamp's `genTime` is honored only if the RFC 3161 token
     * validates and its TSA chain is trusted against this store (c2pa-rs's
     * live timestamp-trust check). With `null`/unset and `requireTrustedTimestamp`
     * set, the timestamp is never trusted, so validity is checked against `now`.
     */
    timestampStore?: TrustStore | null
    /**
     * Apply the trusted-timestamp gate (used for the active manifest, which
     * c2pa-rs re-validates live). When false (ingredients, whose validation is
     * inherited), the embedded `genTime` is honored unconditionally as before.
     */
    requireTrustedTimestamp?: boolean
}

/**
 * Verifies a manifest's claim signature and signing-certificate validity, and
 * builds its signature_info. Certificate validity is checked at the COSE
 * timestamp (`sigTst`/`sigTst2` genTime) when present, otherwise at `nowIso`.
 * For the active manifest (`requireTrustedTimestamp`), the genTime is only
 * honored when the timestamp is trusted; otherwise validity uses `nowIso`.
 */
export async function verifyManifestSignature (m: ParsedManifest, nowIso: string, opts: VerifyManifestOptions = {}): Promise<ManifestVerification> {
    const base: ManifestVerification = { label: m.label, signatureInfo: null, signatureValid: false, expired: false, notYetValid: false, refTime: nowIso, attestedTime: null, cert: null, profileValid: false, sign1: null, chain: [] }
    if (!m.signatureBox) return base

    const sigData = dataChild(m.signatureBox, 'cbor')?.data
    if (!sigData) return base

    let sign1: CoseSign1
    try {
        sign1 = decodeSign1(sigData)
    } catch {
        return base
    }

    const chain = x5chain(sign1)
    let cert: CertInfo | null = null
    try {
        cert = chain.length > 0 ? parseCertificate(chain[0]) : null
    } catch {
        cert = null
    }

    // §13.2.1 (COSE-1): the protected-header alg must be compatible with the leaf
    // key type. §13.2.2 (COSE-6): a detached-payload COSE_Sign1 carries a nil
    // payload — an inline (non-nil) payload is malformed. Both fail closed here
    // (→ signatureValid=false → claimSignature.mismatch) rather than relying on a
    // WebCrypto side-effect or silently ignoring the embedded payload.
    const alg = coseAlg(sign1)
    const algKeyOk = cert && alg !== undefined && coseAlgMatchesKeyType(alg, cert.spkiAlgOid, cert.ecCurveOid)
    const payloadOk = sign1.payload === null
    const signatureValid = (cert && algKeyOk && payloadOk) ? await verifyCoseSign1(sign1, m.claimRaw, cert.spki) : false
    const signatureInfo = cert ? buildSignatureInfo(sign1, cert) : null
    // C2PA end-entity certificate-profile enforcement (§14.5.1.1), independent of trust.
    const profileValid = cert ? checkCertificateProfile(cert).valid : false

    // Certificate validity at the reference time. The active manifest honors the
    // timestamp's genTime only when the timestamp is trusted (matching c2pa-rs's
    // live verify_timestamp_trust check); ingredients honor it unconditionally.
    let refTime: string
    let attestedTime: string | null = null
    if (opts.requireTrustedTimestamp) {
        const ts = await validateTimestamp(sign1, m.claimRaw, opts.timestampStore ?? null)
        attestedTime = ts.trusted ? ts.genTime : null
        refTime = ts.trusted && ts.genTime ? ts.genTime : nowIso
    } else {
        // Ingredient inherited-validation (§15.8.2 / TS-1): honor the embedded genTime
        // only when the token at least VALIDATES (messageImprint + TSA CMS signature +
        // self-consistency, trust not required). A forged/garbage sigTst thus falls back
        // to `now` and can no longer suppress signingCredential.expired on an ingredient.
        const ts = await validateTimestamp(sign1, m.claimRaw, opts.timestampStore ?? null)
        refTime = ts.validated && ts.genTime ? ts.genTime : nowIso
    }
    const expired = cert ? cert.notAfter < refTime : false
    const notYetValid = cert ? refTime < cert.notBefore : false

    return { label: m.label, signatureInfo, signatureValid, expired, notYetValid, refTime, attestedTime, cert, profileValid, sign1, chain }
}
